import React from 'react';
import { IPhraseChatItemProps } from './types';
import { alpha, Box, Button, CircularProgress, Collapse, Fade, IconButton, Stack, SxProps, Tooltip } from '@mui/material';
import { theme } from '../../../../../../common/styles/theme';
import { TextWithSpeakerHOC } from '../../../../../../common/components/TextWithSpeaker/hocs/TextWithSpeakerHOC';
import { RedoIcon } from '../../../../../../../icons/RedoIcon';
import { SettingsIcon } from '../../../../../../../icons/SettingsIcon';
import { DeviceContext } from '../../../../../../common/contexts/DeviceContext';
import { NO_PHRASE_HINT } from './wordings';
import { CopyIcon } from '../../../../../../../icons/CopyIcon';
import { ArrowDropIcon } from '../../../../../../../icons/ArrowDropIcon';
import { CheckIcon } from '../../../../../../../icons/CheckIcon';
import { HtmlUtils } from '../../../../../../common/utils/html-utils';
import { SelectionUtil } from '../../../../../../common/utils/selection-util';

export const PhraseChatItem: React.FC<IPhraseChatItemProps> = ({
	active,
	prompt,
	promptName,
	processing,
	result,
	text,
	canExec,
	id,
	type,
	isNotePopup,
	onClickSettings,
	onLoad,
	onCopyNote,
	onOpenNote,
	onSelection,
}) => {
	const className = 'phrase-chat-item';
	const classOut = `${className}__out`;

	const {isMobile} = React.useContext(DeviceContext);

	const ref = React.useRef(null);
	const [isFlash, setFlash] = React.useState(false);
	const [isOpen, setOpen] = React.useState(false);
	const [isOpenAnim, setOpenAnim] = React.useState(false);
	const [isSaved, setSaved] = React.useState(false);
	
	const onClickSave = () => {
		if (isSaved) {
			if (!isNotePopup) {
				onOpenNote();
				setSaved(false);
			}
		} else {
			onCopyNote();
			setSaved(true);
			setShowButtons(false);

			setTimeout(() => {
				setSaved(false);
			}, 3000);
		}
	};

	React.useEffect(() => {
		if (active && result) {
			setFlash(true);
			setTimeout(() => {
				setFlash(false);
			}, 1000);
			if (ref.current) ref.current.scrollIntoViewIfNeeded();
		} else {
			setFlash(false);
		}
	}, [active, result]);

	React.useEffect(() => {
		setSaved(false);
	}, [result]);

	/*React.useEffect(() => {
		if (canExec) onLoad(true);
	}, [prompt]);*/

	React.useEffect(() => {
		onLoad(true);
	}, [id, type, text, prompt, promptName]);

	const buttonStyle: SxProps = {
		height: theme.spacing(4),
		width: theme.spacing(3),
		p: theme.spacing(1),
		fontSize: theme.typography.fontSize,
		color: theme.palette.grey[400],
	};

	const [isShowButtons, setShowButtons] = React.useState(true);
	const showButtonsTimer = React.useRef(null);
	const [isShowSaveArea, setShowSaveArea] = React.useState(false);
	const isMouseIn = React.useRef(false);

	const onMouseIn = () => {
		if (isMobile) return;
		if (showButtonsTimer.current) clearTimeout(showButtonsTimer.current);
		showButtonsTimer.current = setTimeout(() => {
			setShowButtons(true);
		}, 300) as any;
		isMouseIn.current = true;
	};
	const onMouseOut = () => {
		if (isMobile) return;
		if (showButtonsTimer.current) clearTimeout(showButtonsTimer.current);
		showButtonsTimer.current = setTimeout(() => {
			setShowButtons(false);
		}, 500) as any;
		isMouseIn.current = false;
	};

	const onSaveMouseIn = () => {
		if (isMobile) return;
		setShowSaveArea(true);
	};
	const onSaveMouseOut = () => {
		if (isMobile) return;
		setShowSaveArea(false);
	};

	React.useEffect(() => {
		if (isMobile) return;

		if (!processing) {
			setShowButtons(true);
			setTimeout(() => {
				if (!isMouseIn.current) setShowButtons(false);
			}, 1000);
		} else {
			setShowButtons(false);
		}
	}, [processing]);

	const spaces = [' ', '.', ',', '!', '?', ':', ';'];
	const mouseIsDown = React.useRef(false);
	const selectDebounce = React.useRef<any>(null);

	const onSelectEvent = React.useCallback(() => {
		if (selectDebounce.current) clearTimeout(selectDebounce.current);
		selectDebounce.current = setTimeout(() => {
			if (mouseIsDown.current) return;
			const selection = window.getSelection();
			const anchorNode = selection?.anchorNode;
			const focusNode = selection?.focusNode;
			if (!anchorNode || !focusNode) return;

			if (selection.isCollapsed && !selection.anchorOffset) {
				return;
			}

			let parentsClasses = HtmlUtils.getParentsClasses(anchorNode as HTMLElement, [classOut]);
			if (!parentsClasses.includes(classOut)) return;

			parentsClasses = HtmlUtils.getParentsClasses(focusNode as HTMLElement, [classOut]);
			if (!parentsClasses.includes(classOut)) {
				SelectionUtil.removeWindowSelection();
				return;
			}

			let text = selection?.toString();
			const anchorContent = anchorNode.textContent || '';
			const focusContent = focusNode.textContent || '';
			if (selection.anchorOffset > 0) {
				let i = selection.anchorOffset;
				while (i > 0 &&
					!spaces.includes(anchorContent[i]) && !spaces.includes(anchorContent[i - 1])
				) {
					text = anchorContent[i - 1] + text;
					i--;
				}
			}
			if (selection.focusOffset < focusContent.length - 1) {
				let i = selection.focusOffset - 1;
				while (i < focusContent.length - 1 &&
					!spaces.includes(focusContent[i]) && !spaces.includes(focusContent[i + 1])
				) {
					text += focusContent[i + 1];
					i++;
				}
			}

			if (text) {
				if (onSelection) onSelection(text);
			}
		}, 300);
	}, []);

	const onMouseDown = () => {
		mouseIsDown.current = true;
	}
	const onMouseUp = () => {
		mouseIsDown.current = false;
		if (onSelection) onSelectEvent();
	}
	const onDoubleClick = () => {
		if (selectDebounce.current) clearTimeout(selectDebounce.current);
	}

	React.useEffect(() => {
		if (onSelection) document.addEventListener('selectionchange', onSelectEvent);

		return () => {
			if (selectDebounce.current) clearTimeout(selectDebounce.current);
			document.removeEventListener('selectionchange', onSelectEvent);
		}
	}, [result]);

	
	return (
		<Stack
			className={className}
			gap={theme.spacing(1.5)}
			sx={{
				position: 'relative',
				pr: theme.spacing(1),
			}}
			onMouseEnter={onMouseIn}
			onMouseLeave={onMouseOut}
		>
			<Stack
				sx={{
					position: 'static',
					width: isOpenAnim ? 1 : 'min-content',
					maxWidth: 1,
					transition: 'all .3s ease',
				}}
			>
				<Box
					className={`${className}__prompt ${isOpen ? 'open' : 'close'}
						${!canExec ? 'cannotexec' : ''}
					`}
					sx={{
						flex: `1 1 100%`,
						minWidth: theme.spacing(15),
						color: theme.palette.grey[400],
						cursor: 'pointer',

						'&.cannotexec': {
							opacity: .3,
							cursor: 'default',
						},
					}}
					onClick={() => {
						let val = !isOpen;
						setOpen(val);
						if (val) {
							setOpenAnim(val);
						} else {
							setTimeout(() => setOpenAnim(val),  300);
						}
					}}
				>
					<Stack
						flexDirection={'row'}
						gap={1}
						justifyContent={'space-between'}
						alignItems={'center'}
					>
						<Box
							sx={{
								fontSize: theme.typography.h6,
								fontWeight: 300,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								textTransform: 'capitalize',
							}}
						>
							{promptName}
						</Box>

						
						<Stack
							flexDirection={'row'}
							justifyContent={'flex-end'}
							gap={theme.spacing(.5)}
						>
							{isOpen && canExec && !processing && <>
								{!isMobile && <>
									<Tooltip
										title={'Edit prompt'}
									>
										<IconButton
											onClick={onClickSettings}
											sx={buttonStyle}
										>
											<SettingsIcon sx={{ width: theme.spacing(2), height: theme.spacing(2) }}/>
										</IconButton>
									</Tooltip>
								</>}
								<Tooltip
									title={'Refresh prompt'}
								>
									<IconButton
										onClick={() => onLoad(false)}
										sx={buttonStyle}
									>
										<RedoIcon sx={{ width: theme.spacing(2.5), height: theme.spacing(2.5) }}/>
									</IconButton>
								</Tooltip>
							</>}

							<IconButton
								onClick={() => onLoad(false)}
								sx={buttonStyle}
							>
								<ArrowDropIcon
									sx={{
										transform: isOpen ? 'rotate(180deg)' : '',
										transition: 'all .3s ease',
									}}
								/>
							</IconButton>

						</Stack>

					</Stack>
					<Collapse in={isOpen} appear={false}>
						<Box
							sx={{
								lineHeight: 1.3,
							}}
						>
							{prompt}
						</Box>
					</Collapse>
				</Box>
			</Stack>

			{processing
				? <Stack
					sx={{
						width: 1,
						my: theme.spacing(3),
						alignItems: 'center',
					}}
				>
					<CircularProgress size={theme.spacing(3)} />
				</Stack>
				: <Stack
					sx={{
						position: 'relative',
					}}
				>
					{!canExec
						? <Box
							className={`
								${classOut} key${id}type${type}
							`}
							sx={{
								ml: theme.spacing(-1),
								mb: theme.spacing(2),
								p: theme.spacing(2),
								fontSize: theme.typography.subtitle2.fontSize,
								color: theme.palette.error.main,
								borderRadius: theme.spacing(1),
								backgroundColor: alpha(theme.palette.error.main, .05),
							}}
						>
							{NO_PHRASE_HINT}
						</Box>
						: result &&
						<Box
							className={`
								${className}__out key${id}type${type}
								${isFlash ? 'flash' : ''}
								${isShowSaveArea ? 'show-save-area' : ''}
							`}
							ref={ref}
							sx={{
								position: 'relative',
								pr: theme.spacing(1),

								'&:before': {
									content: '""',
									position: 'absolute',
									left: theme.spacing(-1),
									right: 0,
									top: theme.spacing(-1),
									bottom: theme.spacing(-.5),
									borderRadius: theme.spacing(1),
								},

								'.text-with-speaker p:first-of-type:before': {
									content: '""',
									position: 'absolute',
									top: theme.spacing(0),
									bottom: theme.spacing(0),
									left: theme.spacing(-3),
									width: theme.spacing(3),
								},

								'&.flash:before': {
									animation: 'animation_chat_item_flash .5s 3 ease-in-out',
								},

								'@keyframes animation_chat_item_flash': {
									'100%': { background: theme.palette.primary[100] },
								},

								'&.show-save-area:before': {
									background: alpha(theme.palette.warning[100], .2),
								},
							}}
							onMouseDown={onMouseDown}
							onMouseUp={onMouseUp}
							onDoubleClick={onDoubleClick}
						>
							<TextWithSpeakerHOC
								text={result}
							/>
						</Box>
					}
					{canExec && result &&
						<Fade
							in={isShowButtons || isSaved}
							timeout={{
								exit: 1000,
							}}
						>
							<Stack
								flexDirection={'row'}
								sx={{
									position: 'sticky',
									bottom: theme.spacing(1),
									mt: theme.spacing(.75),
									ml: theme.spacing(-1),
								}}
							>
								<Button
									className={isSaved ? 'saved' : ''}
									variant={'contained'}
									sx={{
										height: theme.spacing(4),
										p: `0 ${theme.spacing(1.25)}`,
										color: theme.palette.warning[900],
										background: theme.palette.warning[100],
			
										'&:hover, &:focus': {
											background: theme.palette.info[200],
										},
			
										'&.saved': {
											color: theme.palette.primary.main,
											background: theme.palette.primary.light,
										},
									}}
									onClick={onClickSave}
									onMouseMove={onSaveMouseIn}
									onMouseLeave={onSaveMouseOut}
								>
									{isSaved
										? <CheckIcon
											sx={{
												width: theme.spacing(1.5),
												ml: theme.spacing(-.5),
												mr: theme.spacing(.5),
											}}
										/>
										: <CopyIcon
											sx={{
												mr: theme.spacing(.5),
											}}
										/>
									}
									{isSaved
										? isNotePopup
											? 'Copied'
											: 'Open saved'
										: 'Copy to note'
									}
								</Button>
							</Stack>
						</Fade>
					}
				</Stack>
			}
			
		</Stack>

	);

}

